import { AppConfig } from "../../contexts/AppConfigContext";
import {
  AssignmentDetailOutput,
  AssignmentListResponseOutput,
  UpdateAssignmentRequestInput,
  UpdateAcceptanceRequest,
  UpdateAcceptanceResponse,
  ProviderServicePointsSummaryOutput,
  ProviderServicePointsRequestInput,
  UpdateAdjustoIdRequest,
  UpdateAdjustoIdResponse,
  UpdateMissionStatusRequest,
  UpdateMissionStatusResponse,
} from "./Assignments.responses";

export class AssignmentsModel {
  private apiBase: string;

  constructor(config: AppConfig) {
    this.apiBase = `${config.assignementsAPI}/v1/api`;
  }

  /**
   * Adds the JWT token to request headers.
   * @param token - JWT token
   * @param headers - Existing headers (optional)
   * @returns Headers with Authorization
   */
  private getAuthHeaders(token: string, headers: HeadersInit = {}): HeadersInit {
    if (!token) {
      throw new Error("No token available. User must be authenticated.");
    }

    return {
      ...headers,
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Generic API call handler with response parsing.
   * @param url - API URL
   * @param token - JWT token
   * @param options - Request options
   * @returns HTTP status code and parsed data
   */
  private async fetchWithAuth<T>(
    url: string,
    token: string,
    options: RequestInit = {},
  ): Promise<{ status: number; data: T | null }> {
    const headers = this.getAuthHeaders(token, options.headers || {});

    const response = await fetch(url, { ...options, headers });

    let data: T | null = null;

    try {
      data = await response.json();
    } catch (err) {
      // Ignore JSON errors (empty response bodies).
    }

    return { status: response.status, data };
  }

  async updateAssignment(
    externalAssignmentId: string,
    updateData: UpdateAssignmentRequestInput,
    token: string,
  ): Promise<{ status: number; data: AssignmentDetailOutput | null }> {
    return this.fetchWithAuth<AssignmentDetailOutput>(
      `${this.apiBase}/assignments/internal/${externalAssignmentId}`,
      token,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      },
    );
  }

  async getAssignmentsByAcceptanceStatus(
    acceptanceStatus: string,
    missionStatus: string,
    token: string,
    page: number = 1,
    size: number = 10,
  ): Promise<{ status: number; data: AssignmentListResponseOutput | null }> {
    return this.fetchWithAuth<AssignmentListResponseOutput>(
      `${this.apiBase}/assignments/internal/acceptance?status=${acceptanceStatus}&missionStatus=${missionStatus}&page=${page}&pageSize=${size}`,
      token,
    );
  }

  async updateAcceptance(
    externalAssignmentId: string,
    updateAcceptanceData: UpdateAcceptanceRequest,
    token: string,
  ): Promise<{ status: number; data: UpdateAcceptanceResponse | null }> {
    return this.fetchWithAuth<UpdateAcceptanceResponse>(
      `${this.apiBase}/assignments/internal/${externalAssignmentId}/acceptance`,
      token,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateAcceptanceData),
      },
    );
  }

  async searchAssignment(
    searchParams: {
      adjustoId?: string;
      externalId?: string;
      claimId?: string;
    },
    token: string,
  ): Promise<{ status: number; data: AssignmentDetailOutput | null }> {
    const queryParams = new URLSearchParams();
    if (searchParams.adjustoId) queryParams.append("adjustoId", searchParams.adjustoId);
    if (searchParams.externalId) queryParams.append("externalId", searchParams.externalId);
    if (searchParams.claimId) queryParams.append("claimId", searchParams.claimId);

    return this.fetchWithAuth<AssignmentDetailOutput>(
      `${this.apiBase}/assignments/internal/search?${queryParams.toString()}`,
      token,
    );
  }

  async getServiceProviders(
    token: string,
  ): Promise<{ status: number; data: ProviderServicePointsSummaryOutput | null }> {
    return this.fetchWithAuth<ProviderServicePointsSummaryOutput>(
      `${this.apiBase}/providers`,
      token,
    );
  }

  async updateProviderInformations(
    providerId: string,
    updateData: ProviderServicePointsRequestInput,
    token: string,
  ): Promise<{ status: number; data: ProviderServicePointsSummaryOutput | null }> {
    return this.fetchWithAuth<ProviderServicePointsSummaryOutput>(
      `${this.apiBase}/providers/${providerId}/provider-informations`,
      token,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      },
    );
  }

  async getAssignment(
    externalAssignmentId: string,
    token: string,
  ): Promise<{ status: number; data: AssignmentDetailOutput | null }> {
    return this.fetchWithAuth<AssignmentDetailOutput>(
      `${this.apiBase}/assignments/internal/${externalAssignmentId}`,
      token,
    );
  }

  async updateAdjustoId(
    externalAssignmentId: string,
    updateData: UpdateAdjustoIdRequest,
    token: string,
  ): Promise<{ status: number; data: UpdateAdjustoIdResponse | null }> {
    return this.fetchWithAuth<UpdateAdjustoIdResponse>(
      `${this.apiBase}/assignments/internal/${externalAssignmentId}/adjustoId`,
      token,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      },
    );
  }

  async updateMissionStatus(
    externalAssignmentId: string,
    updateData: UpdateMissionStatusRequest,
    token: string,
  ): Promise<{ status: number; data: UpdateMissionStatusResponse | null }> {
    return this.fetchWithAuth<UpdateMissionStatusResponse>(
      `${this.apiBase}/assignments/internal/${externalAssignmentId}/missionStatus`,
      token,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      },
    );
  }

  async exportAssignmentsByMonth(
    year: number,
    month: number,
    token: string,
  ): Promise<{ status: number; data: Blob | null }> {
    const headers = this.getAuthHeaders(token);

    const response = await fetch(
      `${this.apiBase}/assignments/internal/export/month?year=${year}&month=${month}`,
      { headers },
    );

    let data: Blob | null = null;

    if (response.status === 200) {
      data = await response.blob();
    }

    return { status: response.status, data };
  }
}
